/* eslint-disable better-mutation/no-mutation */
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { fetchOffsitePaymentDetection } from 'tradera/apps/syi/state/actions/long-description-actions';

const MAX_DESCRIPTION_LENGTH = 7000;

function validate(textWithHtml: string | null) {
    if (textWithHtml && textWithHtml.length > MAX_DESCRIPTION_LENGTH) {
        return 'syi_validation_description_to_long';
    }

    const elm = document.createElement('div');
    elm.innerHTML = textWithHtml || '';

    if (elm.textContent) {
        return elm.textContent.trim() !== ''
            ? null
            : 'syi_validation_missing_description';
    } else if (elm.innerText) {
        return elm.innerText.trim() !== ''
            ? null
            : 'syi_validation_missing_description';
    }
    return 'syi_validation_missing_description';
}

// By ensuring that shortDescription is always a string makes
// it easy to know when it has really changed to something significant.
function ensureString(s?: string | null) {
    return !s ? '' : s;
}

type longDescriptionSlice = {
    longDescription: string | null;
    validationError: string | null;
    isCurrentDescriptionFromDraft: boolean;
    fromDraftCounter: number;
    isOffsitePaymentDetected?: boolean;
};
const initialState: longDescriptionSlice = {
    longDescription: null,
    validationError: null,
    isCurrentDescriptionFromDraft: false,
    fromDraftCounter: 0,
    isOffsitePaymentDetected: false
};

const longDescriptionSlice = createSlice({
    name: 'longDescription',
    initialState,
    reducers: {
        setLongDescription: (state, action: PayloadAction<string | null>) => {
            state.isCurrentDescriptionFromDraft = false;
            state.longDescription = ensureString(action.payload);
            state.validationError = validate(action.payload);
        },
        setLongDescriptionFromDraft: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.isCurrentDescriptionFromDraft = true;
            state.longDescription = ensureString(action.payload);
            state.validationError = validate(action.payload);
            state.fromDraftCounter += 1;
        },
        resetLongDescription: (state) => {
            const defaultValue = null;
            state.isCurrentDescriptionFromDraft = false;
            state.longDescription = defaultValue;
            state.validationError = validate(defaultValue);
        }
    },
    extraReducers: (builder) => {
        builder.addCase(
            fetchOffsitePaymentDetection.fulfilled,
            (state, action) => {
                state.isOffsitePaymentDetected =
                    action?.payload?.isOffsitePaymentDetected || false;
            }
        );
    }
});

export const {
    setLongDescription,
    setLongDescriptionFromDraft,
    resetLongDescription
} = longDescriptionSlice.actions;

export default longDescriptionSlice.reducer;
